import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  InputAdornment,
  Avatar,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
  LocationOn as LocationIcon,
  Hotel as HotelIcon,
  Restaurant as RestaurantIcon,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { contentApi, type CityListItem } from '../../services/api-client';
import { useAuth } from '../../contexts/GoogleAuthContext';
import ContentNav from '../../components/content/ContentNav';

type SortKey = 'name' | 'slug';
type SortDir = 'asc' | 'desc';

export default function CityList() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const canEdit =
    user?.role === 'admin' || user?.effective_permissions?.content?.edit === true;

  // Fetch cities
  const {
    data: cities,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['content', 'cities'],
    queryFn: () => contentApi.getCities(),
    staleTime: 5 * 60 * 1000,
  });

  // Sort handler
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  // Filter + sort
  const filtered = useMemo(() => {
    if (!cities) return [];
    let items = [...cities];

    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.subtitle?.toLowerCase().includes(q) ||
          c.slug.toLowerCase().includes(q)
      );
    }

    items.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'slug':
          cmp = a.slug.localeCompare(b.slug);
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return items;
  }, [cities, search, sortKey, sortDir]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load city guides. Please check that the Content API is reachable.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Content section tabs */}
      <ContentNav />

      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          City Guides
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {cities?.length ?? 0} cities with curated content
        </Typography>
      </Box>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search cities..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        {search && (
          <Chip
            label="Clear"
            size="small"
            onDelete={() => setSearch('')}
          />
        )}
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 56 }} />
              <TableCell>
                <TableSortLabel
                  active={sortKey === 'name'}
                  direction={sortKey === 'name' ? sortDir : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  City
                </TableSortLabel>
              </TableCell>
              <TableCell>Subtitle</TableCell>
              <TableCell>Coordinates</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {search ? 'No cities match your search.' : 'No city guides found.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {filtered.map((city) => (
              <TableRow
                key={city.slug}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/content/cms/cities/${city.slug}`)}
              >
                {/* Image */}
                <TableCell sx={{ pr: 0 }}>
                  <Avatar
                    variant="rounded"
                    src={city.heroImage}
                    alt={city.name}
                    sx={{ width: 44, height: 44 }}
                  >
                    <LocationIcon />
                  </Avatar>
                </TableCell>

                {/* Name */}
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {city.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    /{city.slug}
                  </Typography>
                </TableCell>

                {/* Subtitle */}
                <TableCell>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      maxWidth: 350,
                    }}
                  >
                    {city.subtitle}
                  </Typography>
                </TableCell>

                {/* Coordinates */}
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {city.geoCode?.lat?.toFixed(4)}, {city.geoCode?.long?.toFixed(4)}
                  </Typography>
                </TableCell>

                {/* Actions */}
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  {canEdit && (
                    <Tooltip title="Edit city">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/content/cms/cities/${city.slug}`)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="View locations">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/content/cms/cities/${city.slug}/locations`)}
                    >
                      <HotelIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="View on site">
                    <IconButton
                      size="small"
                      component="a"
                      href={`https://curatedby.tripsby.ai/cities/${city.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 1, px: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Showing {filtered.length} of {cities?.length ?? 0} cities
        </Typography>
      </Box>
    </Box>
  );
}
